/**
 * Purpose: These selectors operate on the "offer" node of the state instead of using the full state like typical selectors do.
 */

import type { Offer, OfferType, PaymentTerm, TermsById } from '@makemydeal/dr-platform-types';
import { BALLOON, FINANCE, OFFER_TYPE } from '@makemydeal/dr-platform-types';

export const getAllTermsFromOffer = (offer: Offer): TermsById => {
    return offer?.terms?.byId || {};
};

/**
 * @deprecated Use getAllTermsFromOffer instead.
 */
export function getTermsByIdForOffer(offer: Offer) {
    return getAllTermsFromOffer(offer);
}
/*
 * The following are for adding the incentive total to the offer reducer based on offer.
 * In future sprints, we can replace the corresponding functions that require the entire state
 * with this new set of functions.
 */
export const getTermMonthByOfferTypeFromOffer = (offer: Offer, offerType: OfferType): number | null => {
    let termType: 'financeTerms' | 'leaseTerms' | 'balloonTerms';
    if (offerType === OFFER_TYPE.finance) {
        termType = 'financeTerms';
    } else if (offerType === BALLOON) {
        termType = 'balloonTerms';
    } else {
        termType = 'leaseTerms';
    }
    const offerTermId = offer.penciledInputs?.byOfferType![offerType].term;
    if (offer.terms[termType]?.length) {
        const allTermsFromOffer = getAllTermsFromOffer(offer);
        return allTermsFromOffer[offerTermId!] ? allTermsFromOffer[offerTermId!].months : null;
    }
    return null;
};

export const getTermsIdByOfferTerms = (offer: Offer, offerType: OfferType): string[] => {
    let offerTermsType: 'financeTerms' | 'leaseTerms' | 'balloonTerms';
    if (offerType === FINANCE) {
        offerTermsType = 'financeTerms';
    } else if (offerType === BALLOON) {
        offerTermsType = 'balloonTerms';
    } else {
        offerTermsType = 'leaseTerms';
    }
    return offer.terms[offerTermsType] || [];
};

export const getTermByMonthFromOffer = (offer: Offer, offerType: OfferType): PaymentTerm => {
    const month = getTermMonthByOfferTypeFromOffer(offer, offerType);
    const termsIdByOfferType = getTermsIdByOfferTerms(offer, offerType);
    const allTerms = getAllTermsFromOffer(offer);
    const selectedTerm = termsIdByOfferType.filter((id) => {
        return allTerms[id]?.months === month;
    });
    return (allTerms && allTerms[selectedTerm[0]]) || ({} as PaymentTerm);
};

export const getTermMonthsIdByOfferTypeFromOffer = (offer: Offer, offerType: OfferType): string | undefined => {
    const selectedOfferDetails = offer.penciledInputs?.byOfferType![offerType];
    const term = selectedOfferDetails?.term;
    const terms = getAllTermsFromOffer(offer);
    const termsByMonth = getTermByMonthFromOffer(offer, offerType);

    return (terms[term!] && terms[term!].id) || (termsByMonth && termsByMonth.id);
};

export const getTermFromOffer = (offer: Offer, termId: string): PaymentTerm => {
    const terms = getAllTermsFromOffer(offer);
    const termById = terms[termId];
    const termMonthsId = getTermMonthsIdByOfferTypeFromOffer(offer, offer.offerType as OfferType);
    const termByMonth = terms[termMonthsId as string];
    return termById || termByMonth || ({} as PaymentTerm);
};
